// FaceLock Ink Scan: where a tattoo, scar, mole or birthmark can be, on the
// person's OWN body (their left is "left"; saved pictures are never mirrored).
// The page lists these; the server (FaceShot-ChopShop-web/services/facelock-crew/
// marks.js) holds the same ids with their words, and a test keeps the two equal.
//
// SECTIONS (owner direction 2026-10-06: "some of us are literally covered"):
// the Ink Scan is done one part of the body at a time, only the parts that
// will show in what the person is making now. A later Ink Scan of another
// part ADDS to what the avatar has; it never starts over.
//
// section: head | face_neck | arms_hands | torso | legs_feet (the order the
// page lists them). region: head | face | neck | body (how a mark is
// pictured and named). side: left | right | center | back. always: named in
// every caption and every render (it shows in most outfits); the place alone
// decides it, never the person (owner direction 2026-10-07: the choice
// confused people). helper: the person cannot see it with the front camera
// (someone holds the phone, or it is propped up).
//
// More face places (owner direction 2026-10-07: he has many face tattoos):
// the ears and sideburns, each side of the forehead and nose, the
// cheekbones, the corners of the mouth, below the lower lip. An id is never
// renamed or removed: the server drops a stored mark whose place it does not
// know.

const P = (id, section, region, side, always, helper = side === "back") => Object.freeze({ id, section, region, side, always, helper });

export const MARK_SECTIONS = Object.freeze(["head", "face_neck", "arms_hands", "torso", "legs_feet"]);

export const MARK_PLACE_LIST = Object.freeze([
  P("head_top", "head", "head", "center", true, true),
  P("head_left", "head", "head", "left", true),
  P("head_right", "head", "head", "right", true),
  P("ear_left", "head", "head", "left", true),
  P("ear_right", "head", "head", "right", true),
  P("behind_ear_left", "head", "head", "left", true),
  P("behind_ear_right", "head", "head", "right", true),
  P("head_back", "head", "head", "back", false),
  P("forehead", "face_neck", "face", "center", true),
  P("forehead_left", "face_neck", "face", "left", true),
  P("forehead_right", "face_neck", "face", "right", true),
  P("between_brows", "face_neck", "face", "center", true),
  P("brow_left", "face_neck", "face", "left", true),
  P("brow_right", "face_neck", "face", "right", true),
  P("temple_left", "face_neck", "face", "left", true),
  P("temple_right", "face_neck", "face", "right", true),
  P("sideburn_left", "face_neck", "face", "left", true),
  P("sideburn_right", "face_neck", "face", "right", true),
  P("eye_left", "face_neck", "face", "left", true),
  P("eye_right", "face_neck", "face", "right", true),
  P("under_eye_left", "face_neck", "face", "left", true),
  P("under_eye_right", "face_neck", "face", "right", true),
  P("cheekbone_left", "face_neck", "face", "left", true),
  P("cheekbone_right", "face_neck", "face", "right", true),
  P("cheek_left", "face_neck", "face", "left", true),
  P("cheek_right", "face_neck", "face", "right", true),
  P("nose", "face_neck", "face", "center", true),
  P("nose_left", "face_neck", "face", "left", true),
  P("nose_right", "face_neck", "face", "right", true),
  P("lip", "face_neck", "face", "center", true),
  P("mouth_left", "face_neck", "face", "left", true),
  P("mouth_right", "face_neck", "face", "right", true),
  P("below_lip", "face_neck", "face", "center", true),
  P("chin", "face_neck", "face", "center", true),
  P("jaw_left", "face_neck", "face", "left", true),
  P("jaw_right", "face_neck", "face", "right", true),
  P("neck_front", "face_neck", "neck", "center", true),
  P("neck_left", "face_neck", "neck", "left", true),
  P("neck_right", "face_neck", "neck", "right", true),
  P("neck_back", "face_neck", "neck", "back", false),
  P("arm_left", "arms_hands", "body", "left", false),
  P("arm_right", "arms_hands", "body", "right", false),
  P("shoulder_left", "arms_hands", "body", "left", false),
  P("shoulder_right", "arms_hands", "body", "right", false),
  P("upper_arm_left", "arms_hands", "body", "left", false),
  P("upper_arm_right", "arms_hands", "body", "right", false),
  P("forearm_left", "arms_hands", "body", "left", false),
  P("forearm_right", "arms_hands", "body", "right", false),
  P("hand_left", "arms_hands", "body", "left", true),
  P("hand_right", "arms_hands", "body", "right", true),
  P("fingers_left", "arms_hands", "body", "left", true),
  P("fingers_right", "arms_hands", "body", "right", true),
  P("chest", "torso", "body", "center", false),
  P("stomach", "torso", "body", "center", false),
  P("ribs_left", "torso", "body", "left", false),
  P("ribs_right", "torso", "body", "right", false),
  P("back_full", "torso", "body", "back", false),
  P("back_upper", "torso", "body", "back", false),
  P("back_lower", "torso", "body", "back", false),
  P("leg_left", "legs_feet", "body", "left", false),
  P("leg_right", "legs_feet", "body", "right", false),
  P("thigh_left", "legs_feet", "body", "left", false),
  P("thigh_right", "legs_feet", "body", "right", false),
  P("lower_leg_left", "legs_feet", "body", "left", false),
  P("lower_leg_right", "legs_feet", "body", "right", false),
  P("foot_left", "legs_feet", "body", "left", false),
  P("foot_right", "legs_feet", "body", "right", false),
]);

export const MARK_KINDS = Object.freeze(["tattoo", "scar", "mole", "birthmark"]);
export const MARK_REGIONS = Object.freeze(["head", "face", "neck", "body"]);
/**
 * The most marks one Ink Scan takes, and the longest "what it is". 12, not 6
 * (owner direction 2026-10-07: he has many face tattoos, and two scans cost a
 * whole second enhancement). 12 is what the server's hard caps hold (the 24
 * compare shots, with the views making room; the 48 pictures, checked before
 * a scan starts; the 12 earlier close-ups mixed into training); past 12 they
 * fail. The server's ENHANCE_MAX_MARKS is the same; a test keeps them equal.
 */
export const MARK_MAX = 12;
export const MARK_WHAT_MAX = 60;
/**
 * The most pictures one enhancement sends, Ink and SharpShoot together: the
 * server refuses more (enhance.js PHOTOS_MAX; the trainer takes 48). With 12
 * marks the page checks it BEFORE the scans start (inkPictureCount), so
 * nobody scans for minutes to be refused after the upload.
 */
export const ENHANCE_PHOTOS_MAX = 48;

export function markPlace(id) {
  return MARK_PLACE_LIST.find((p) => p.id === id) || null;
}

/** The places of a section, in page order. */
export function sectionPlaces(section) {
  return MARK_PLACE_LIST.filter((p) => p.section === section);
}

/** The section a place belongs to, or null. */
export function sectionOf(place) {
  const p = markPlace(place);
  return p ? p.section : null;
}

/**
 * How much of each section the avatar has on file: {section: count of
 * marks}, every section present (0 when none). `marks`: [{place}].
 */
export function sectionCounts(marks) {
  const out = Object.fromEntries(MARK_SECTIONS.map((s) => [s, 0]));
  for (const m of marks || []) {
    const s = sectionOf(m && m.place);
    if (s) out[s] += 1;
  }
  return out;
}

/** The views an Ink Scan takes of a mark at `place`, in order. */
export function inkViews(place) {
  const p = markPlace(place);
  if (!p) return [];
  return p.region === "body" ? ["wide", "close_a", "close_b"] : ["close_a", "close_b"];
}

/** How many pictures an Ink Scan of marks at `places` takes: 2 for a head, face or neck mark, 3 for a body mark. */
export function inkPictureCount(places) {
  return (places || []).reduce((n, place) => n + inkViews(place).length, 0);
}

/** A mark the person cannot see with the front camera (the back, the top of the head): the back camera, held by someone else or propped up. */
export function needsHelper(place) {
  const p = markPlace(place);
  return Boolean(p && p.helper);
}
